import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, hasAuthMarker, clearAuthMarker } from "../lib/api";
import { logError } from "../lib/log";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (error) {
      if (error.response?.status !== 401) logError("Session check failed", error);
      // stale marker: stop re-probing /auth/me on every load
      clearAuthMarker();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // No auth data is kept in browser storage: the token is in an httpOnly cookie and
    // this readable marker cookie only says "a session may exist".
    if (!hasAuthMarker()) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      logError("Logout request failed", error);
    }
    clearAuthMarker();
    setUser(null);
  }, []);

  const adoptSession = useCallback((sessionUser) => setUser(sessionUser), []);

  const value = useMemo(
    () => ({ user, setUser, loading, login, signup, logout, checkAuth, adoptSession }),
    [user, loading, login, signup, logout, checkAuth, adoptSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
